import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { fetchGradingCatalog, mintNFT } from "../api/nft";
import { AuthContext } from "../contexts/AuthContext";
import { listCompanies } from "../api/companies";
import FreeTextCombobox from "../components/FreeTextCombobox";
import {
  getWeightInputUnit,
  validateCertificationNumber,
  validateGradeAttributeIntOptional,
  validateGradeAttributeTextOptional,
  validateGradeTermCode,
  validateGradingSystem,
  validateCollectionName,
  validateCountry,
  validateDenomination,
  validateDescription,
  validateEmail,
  extractSheldonNumeric,
  METAL_SUGGESTION_KEYS,
  validateMint,
  normalizeInput,
  normalizeOptionalMetal,
  parseSlabTotalWeightToGrams,
  sanitizeSlabWeightInput,
  sanitizeYearInput,
  validateSlabTotalWeight,
  validateMetal,
  validateSheldonGrade,
  validateNumistrailId,
  validateYear,
} from "../utils/validation";
import { useTranslation } from "react-i18next";
import { resolveApiMessage } from "../utils/apiMessages";
import { CountryAuthorityPicker } from "../components/CountryAuthorityPicker";
import { MintPicker } from "../components/MintPicker";

const Field = ({ id, label, hint, error, children }) => (
  <div className="cert-field">
    <label className="cert-field__label" htmlFor={id}>
      {label}
    </label>
    {React.isValidElement(children) ? React.cloneElement(children, { id }) : children}
    {hint ? <p className="cert-field__hint">{hint}</p> : null}
    {error ? (
      <p className="cert-field__error" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);

const Card = ({ children }) => <div className="cert-card">{children}</div>;

export function CertDashboard({ mode = "trusted" }) {
  const isUserGeneratedMode = mode === "user_generated";
  const requiresCertificationPermission = !isUserGeneratedMode;
  const navigate = useNavigate();
  const redirectTimeoutRef = useRef(null);
  const initial = {
    cert: "",
    auth: "",
    owner: "",
    ownerEmail: "",
    country: "",
    mint: "",
    denomination: "",
    year: "",
    metal: "",
    slab_total_weight: "",
    gradingSystem: "sheldon",
    gradeMode: "numeric", // "numeric" | "details" | "authentic" (Sheldon)
    grade: "",
    detailsTerm: "",
    strikeChar: "",
    bonusPlus: false,
    bonusStar: false,
    termCode: "",
    strike: "",
    surface: "",
    style: "",
    detailsFlag: false,
    detailsReason: "",
    collection: "",
    desc: "",
  };
  const [form, setForm] = useState(initial);
  const [ownerMode, setOwnerMode] = useState("id");
  const [message, setMessage] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [authorities, setAuthorities] = useState([]);
  const [selectedCountryAuthority, setSelectedCountryAuthority] = useState(null);
  const [selectedMint, setSelectedMint] = useState(null);
  const [gradingCatalog, setGradingCatalog] = useState({ systems: [], terms: {}, attribute_definitions: {} });
  const [touched, setTouched] = useState({});
  const { company, canCertify } = useContext(AuthContext);
  const isCertificationEntity = company?.company_type === "CERTIFICATION_ENTITY";
  const { t, i18n } = useTranslation();
  const weightInputLocale = i18n.resolvedLanguage || i18n.language;
  const weightInputUnit = getWeightInputUnit(weightInputLocale);
  const slabWeightUnitLabel = t(
    weightInputUnit === "oz"
      ? "cert.fields.slabWeight.unit.ounces"
      : "cert.fields.slabWeight.unit.grams"
  );
  const slabWeightPlaceholder = t(
    weightInputUnit === "oz"
      ? "cert.fields.slabWeight.placeholder.ounces"
      : "cert.fields.slabWeight.placeholder.grams"
  );
  const isSheldon = (form.gradingSystem || "").toLowerCase() === "sheldon";
  const metalSuggestions = useMemo(
    () => METAL_SUGGESTION_KEYS.map((key) => t(`cert.fields.metal.suggestions.${key}`)),
    [i18n.resolvedLanguage, t]
  );

  useEffect(() => {
    listCompanies({ companyType: "CERTIFICATION_ENTITY" })
      .then((resp) => setAuthorities(resp.data.companies || []))
      .catch(() => setAuthorities([]));
  }, []);

  useEffect(() => {
    fetchGradingCatalog()
      .then(({ data }) => {
        const catalog = data && typeof data === "object" ? data : {};
        setGradingCatalog({
          systems: Array.isArray(catalog.systems) ? catalog.systems : [],
          terms: catalog.terms && typeof catalog.terms === "object" ? catalog.terms : {},
          attribute_definitions:
            catalog.attribute_definitions && typeof catalog.attribute_definitions === "object"
              ? catalog.attribute_definitions
              : {},
        });
      })
      .catch(() => {
        setGradingCatalog({ systems: [], terms: {}, attribute_definitions: {} });
      });
  }, []);

  useEffect(() => {
    if (requiresCertificationPermission && isCertificationEntity && company?.slug) {
      setForm((previous) => ({ ...previous, auth: company.slug }));
    }
  }, [requiresCertificationPermission, isCertificationEntity, company?.slug]);

  useEffect(() => () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
  }, []);

  const handleChange = (key) => (event) => {
    const value = event.target.type === "checkbox" ? event.target.checked : event.target.value;
    const normalizedValue = key === "ownerEmail"
      ? String(value || "").toLowerCase()
      : key === "slab_total_weight"
        ? sanitizeSlabWeightInput(value, weightInputLocale)
        : key === "year"
          ? sanitizeYearInput(value)
        : value;
    setForm((previous) => {
      const next = { ...previous, [key]: normalizedValue };
      if (key === "gradingSystem") {
        const system = normalizeInput(value, { lowercase: true });
        if (system === "ancient_descriptive") {
          const termOptions = gradingCatalog?.terms?.ancient_descriptive;
          if (!next.termCode && Array.isArray(termOptions) && termOptions.length) {
            next.termCode = termOptions[0]?.code || "";
          }
          next.grade = "";
          next.gradeMode = "numeric";
          next.detailsTerm = "";
          next.strikeChar = "";
          next.bonusPlus = false;
          next.bonusStar = false;
        } else {
          next.termCode = "";
          next.strike = "";
          next.surface = "";
          next.style = "";
          next.detailsFlag = false;
          next.detailsReason = "";
          next.gradeMode = "numeric";
          next.detailsTerm = "";
          next.strikeChar = "";
          next.bonusPlus = false;
          next.bonusStar = false;
        }
      } else if (key === "detailsFlag") {
        if (!value) {
          next.detailsReason = "";
        }
      } else if (key === "gradeMode") {
        const mode = String(value || "");
        if (mode === "details") {
          next.grade = "";
          next.bonusPlus = false;
          next.bonusStar = false;
          next.strikeChar = ""; // reset; only colors are allowed in details
        } else if (mode === "authentic") {
          next.grade = "";
          next.detailsTerm = "";
          next.bonusPlus = false;
          next.bonusStar = false;
          next.strikeChar = "";
        } else {
          next.detailsTerm = "";
        }
      }
      return next;
    });
    if (key === "country") {
      setSelectedCountryAuthority(null);
      setSelectedMint(null);
    }
    if (key === "year" || key === "mint") {
      setSelectedMint(null);
    }
    setTouched((prev) => (prev[key] ? prev : { ...prev, [key]: true }));
  };

  const markTouched = (key) => () =>
    setTouched((prev) => ({ ...prev, [key]: true }));

  const handleCountryInputChange = (nextValue) => {
    setForm((previous) => ({ ...previous, country: nextValue }));
    setTouched((prev) => (prev.country ? prev : { ...prev, country: true }));
  };

  const handleCountrySelect = (authority) => {
    setSelectedCountryAuthority(authority || null);
    setSelectedMint(null);
    setTouched((prev) => (prev.country ? prev : { ...prev, country: true }));
  };

  const handleCountryClear = () => {
    setSelectedCountryAuthority(null);
    setSelectedMint(null);
    setTouched((prev) => (prev.country ? prev : { ...prev, country: true }));
  };

  const handleMintInputChange = (nextValue) => {
    setForm((previous) => ({ ...previous, mint: nextValue }));
    setTouched((prev) => (prev.mint ? prev : { ...prev, mint: true }));
  };

  const handleMintSelect = (mintOption) => {
    setSelectedMint(mintOption || null);
    setTouched((prev) => (prev.mint ? prev : { ...prev, mint: true }));
  };

  const handleMintClear = () => {
    setSelectedMint(null);
    setTouched((prev) => (prev.mint ? prev : { ...prev, mint: true }));
  };

  const validationErrors = useMemo(() => {
    const slugSet = new Set(authorities.map((authority) => authority.slug));
    const system = normalizeInput(form.gradingSystem || "sheldon", { lowercase: true }) || "sheldon";
    const detailsMode = system === "sheldon" && form.gradeMode === "details";
    const authenticMode = system === "sheldon" && form.gradeMode === "authentic";
    const shouldValidateDetailsReason = system === "ancient_descriptive" && form.detailsFlag;
    const ownerError = requiresCertificationPermission
      ? ownerMode === "id"
        ? validateNumistrailId(form.owner)
        : validateEmail(form.ownerEmail)
      : null;
    return {
      auth: validateAuthority(form.auth, slugSet, authorities.length > 0),
      cert: validateCertificationNumber(form.cert),
      country: validateCountry(form.country),
      mint: validateMint(form.mint),
      denomination: validateDenomination(form.denomination),
      year: validateYear(form.year),
      metal: validateMetal(form.metal),
      slab_total_weight: validateSlabTotalWeight(form.slab_total_weight, weightInputLocale),
      gradingSystem: validateGradingSystem(form.gradingSystem),
      grade: system === "sheldon" && !detailsMode && !authenticMode ? validateSheldonGrade(form.grade) : null,
      termCode: system === "ancient_descriptive" ? validateGradeTermCode(form.termCode) : null,
      detailsTerm: detailsMode ? validateGradeTermCode(form.detailsTerm) : null,
      strike: system === "ancient_descriptive" ? validateGradeAttributeIntOptional(form.strike) : null,
      surface: system === "ancient_descriptive" ? validateGradeAttributeIntOptional(form.surface) : null,
      style: system === "ancient_descriptive" ? validateGradeAttributeTextOptional(form.style) : null,
      detailsReason: shouldValidateDetailsReason ? validateGradeAttributeTextOptional(form.detailsReason) : null,
      owner: ownerError,
      collection: validateCollectionName(form.collection),
      desc: validateDescription(form.desc),
    };
  }, [
    form.auth,
    form.cert,
    form.country,
    form.mint,
    form.denomination,
    form.year,
    form.metal,
    form.slab_total_weight,
    form.gradingSystem,
    form.gradeMode,
    form.grade,
    form.detailsTerm,
    form.termCode,
    form.strike,
    form.surface,
    form.style,
    form.detailsReason,
    form.detailsFlag,
    form.owner,
    form.ownerEmail,
    form.collection,
    form.desc,
    authorities,
    ownerMode,
    requiresCertificationPermission,
    weightInputLocale,
  ]);

  const isValid = useMemo(
    () => Object.values(validationErrors).every((error) => !error),
    [validationErrors]
  );
  const ownerFieldKey = ownerMode === "id" ? "owner" : "ownerEmail";
  const ownerValue = ownerMode === "id" ? form.owner : form.ownerEmail;
  const ownerLabel = ownerMode === "id" ? t("cert.fields.owner.label") : t("cert.fields.ownerEmail.label");
  const ownerHint = ownerMode === "id" ? t("cert.fields.owner.hint") : t("cert.fields.ownerEmail.hint");
  const ownerPlaceholder = ownerMode === "id" ? t("cert.fields.owner.placeholder") : t("cert.fields.ownerEmail.placeholder");
  const ownerTouched = ownerMode === "id" ? touched.owner : touched.ownerEmail;
  const ownerError = ownerTouched && validationErrors.owner ? t(validationErrors.owner) : null;

  const go = async () => {
    if (!isValid || isSubmitting || (requiresCertificationPermission && !canCertify)) {
      if (!isValid) {
        setTouched({
          auth: true,
          cert: true,
          country: true,
          mint: true,
          denomination: true,
          year: true,
          metal: true,
          slab_total_weight: true,
          gradingSystem: true,
          grade: true,
          gradeMode: true,
          detailsTerm: true,
          termCode: true,
          strike: true,
          surface: true,
          style: true,
          detailsReason: true,
          owner: true,
          collection: true,
          desc: true,
        });
      }
      return;
    }

    const selectedAuthority = authorities.find((authority) => authority.slug === form.auth);
    const normalizedCertification = normalizeInput(form.cert);
    const normalizedOwner = normalizeInput(form.owner, { lowercase: true });
    const normalizedOwnerEmail = normalizeInput(form.ownerEmail, { lowercase: true });
    const normalizedCountry = normalizeInput(form.country);
    const normalizedMint = normalizeInput(form.mint);
    const normalizedDenomination = normalizeInput(form.denomination);
    const normalizedYear = Number(normalizeInput(form.year));
    const normalizedMetal = normalizeOptionalMetal(form.metal);
    const normalizedSlabWeightGrams = parseSlabTotalWeightToGrams(form.slab_total_weight, weightInputLocale);
    const normalizedSystem = normalizeInput(form.gradingSystem || "sheldon", { lowercase: true }) || "sheldon";
    const normalizedGrade = normalizeInput(form.grade);
    const gradeMode = form.gradeMode === "details" ? "details" : form.gradeMode === "authentic" ? "authentic" : "numeric";
    const detailsTermCode = normalizeInput(form.detailsTerm, { uppercase: true });
    const normalizedTermCode = normalizeInput(form.termCode, { uppercase: true });
    const numericValue =
      normalizedSystem === "sheldon" && gradeMode !== "details" ? extractSheldonNumeric(normalizedGrade) : null;
    const normalizedStyle = normalizeInput(form.style);
    const normalizedDetailsReason = normalizeInput(form.detailsReason);
    const isSheldon = normalizedSystem === "sheldon";

    const attributes = [];
    if (normalizedSystem === "ancient_descriptive") {
      const strikeValue = normalizeInput(form.strike);
      const surfaceValue = normalizeInput(form.surface);
      if (strikeValue) {
        attributes.push({ code: "strike", value_int: Number(strikeValue) });
      }
      if (surfaceValue) {
        attributes.push({ code: "surface", value_int: Number(surfaceValue) });
      }
      if (normalizedStyle) {
        attributes.push({ code: "style", value_text: normalizedStyle });
      }
    }

    const details =
      normalizedSystem === "ancient_descriptive"
        ? {
            flag: Boolean(form.detailsFlag),
            ...(normalizedDetailsReason ? { reason: normalizedDetailsReason } : {}),
          }
        : undefined;

    const designations = [];
    if (isSheldon) {
      const strikeSlug = normalizeInput(form.strikeChar, { lowercase: true });
      if (gradeMode === "numeric") {
        if (strikeSlug) designations.push(strikeSlug);
        if (form.bonusPlus) designations.push("plus");
        if (form.bonusStar) designations.push("star");
      } else if (gradeMode === "details") {
        // only color strikes allowed; no bonus
        if (["rd", "rb", "bn"].includes(strikeSlug)) designations.push(strikeSlug);
      }
      // authentic: no designations
    }

    const gradeInfo = {
      system: normalizedSystem,
      ...(isSheldon
        ? gradeMode === "details"
          ? { term_code: detailsTermCode || undefined, details: { flag: true } }
          : gradeMode === "authentic"
            ? { term_code: "AUTHENTIC" }
            : { numeric_value: numericValue }
        : { term_code: normalizedTermCode, ...(details ? { details } : {}) }),
      ...(attributes.length ? { attributes } : {}),
      ...(designations.length ? { designations } : {}),
    };
    const normalizedCollection = normalizeInput(form.collection);
    const normalizedDescription = normalizeInput(form.desc);
    const detailsDisplay = detailsTermCode ? `${detailsTermCode.replace("_DETAILS", "")} Details` : "";
    const gradeDisplay =
      normalizedSystem === "sheldon"
        ? gradeMode === "details"
          ? detailsDisplay
          : gradeMode === "authentic"
            ? "Authentic"
          : normalizedGrade
        : undefined;

    const payload = {
      certification_number: normalizedCertification,
      certification_authority: selectedAuthority?.display_name || form.auth,
      category: "coin",
      ...(normalizedSystem === "sheldon" ? { grade: gradeDisplay } : {}),
      grade_info: gradeInfo,
      country: normalizedCountry,
      ...(selectedCountryAuthority?.code ? { country_ref_code: selectedCountryAuthority.code } : {}),
      ...(normalizedMint ? { mint: normalizedMint } : {}),
      ...(selectedMint?.code ? { mint_ref_code: selectedMint.code } : {}),
      denomination: normalizedDenomination,
      year: normalizedYear,
      ...(normalizedMetal ? { metal: normalizedMetal } : {}),
      ...(normalizedSlabWeightGrams !== null ? { slab_total_weight_grams: normalizedSlabWeightGrams } : {}),
      ...(normalizedCollection ? { collection_name: normalizedCollection } : {}),
      ...(normalizedDescription ? { description: normalizedDescription } : {}),
    };
    payload.item_origin = isUserGeneratedMode ? "USER_GENERATED" : "TRUSTED_CERTIFIED";
    if (requiresCertificationPermission) {
      if (ownerMode === "id") {
        payload.owner = normalizedOwner;
      } else {
        payload.owner_email = normalizedOwnerEmail;
      }
    }

    setIsSubmitting(true);
    setMessage(null);

    try {
      const resp = await mintNFT(payload);
      setMessage({
        kind: "success",
        text: requiresCertificationPermission
          ? t("cert.mintSuccess", { id: resp.data.id, owner: ownerMode === "id" ? normalizedOwner : normalizedOwnerEmail })
          : t("mint.createSuccess", { id: resp.data.id }),
      });
      setForm({
        cert: "",
        owner: "",
        ownerEmail: "",
        country: "",
        mint: "",
        denomination: "",
        year: "",
        metal: "",
        slab_total_weight: "",
        gradingSystem: "sheldon",
        gradeMode: "numeric",
        grade: "",
        detailsTerm: "",
        strikeChar: "",
        bonusPlus: false,
        bonusStar: false,
        termCode: "",
        strike: "",
        surface: "",
        style: "",
        detailsFlag: false,
        detailsReason: "",
        collection: "",
        desc: "",
        auth: requiresCertificationPermission && isCertificationEntity && company?.slug ? company.slug : "",
      });
      setTouched({});
      setSelectedCountryAuthority(null);
      setSelectedMint(null);
      setOwnerMode("id");
      if (isUserGeneratedMode) {
        redirectTimeoutRef.current = setTimeout(() => {
          navigate("/owner");
        }, 1800);
      }
    } catch (error) {
      setMessage({
        kind: "error",
        text: resolveApiMessage(
          error,
          t,
          requiresCertificationPermission ? "cert.mintFailed" : "mint.createFailed",
          requiresCertificationPermission ? "Mint failed. Please try again." : "Creation failed. Please try again."
        ),
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    await go();
  };

  const handleFormKeyDown = (event) => {
    const tagName = String(event.target?.tagName || "").toLowerCase();
    if (event.key === "Enter" && tagName !== "textarea" && tagName !== "button") {
      event.preventDefault();
    }
  };

  const strikeOptions = useMemo(() => {
    if (!isSheldon || form.gradeMode === "authentic") return [];
    return form.gradeMode === "details" ? ["RD", "RB", "BN"] : ["RD", "RB", "BN", "FB", "FS"];
  }, [form.gradeMode, isSheldon]);
  const pageTitle = requiresCertificationPermission ? t("cert.title") : t("mint.title");
  const pageSubtitle = requiresCertificationPermission ? t("cert.subtitle") : t("mint.subtitle");
  const submitLabel = requiresCertificationPermission ? t("cert.mintCta") : t("mint.createCta");
  const submitProgressLabel = requiresCertificationPermission ? t("cert.minting") : t("mint.creating");
  const isSubmitDisabled = !isValid || isSubmitting || (requiresCertificationPermission && !canCertify);
  const shouldBlockUserGeneratedMint = isUserGeneratedMode && isCertificationEntity;

  if (shouldBlockUserGeneratedMint) {
    return <Navigate to={canCertify ? "/cert" : "/owner"} replace />;
  }

  return (
    <div className="cert-screen">
      <Card>
          <div className="cert-card__header">
            <h2 className="cert-card__title">{pageTitle}</h2>
            <p className="cert-card__subtitle">
              {pageSubtitle}
            </p>
            {requiresCertificationPermission && (
              <p className="cert-card__company">
                {t("cert.company", { company: company?.display_name || t("common.notAvailable") })}
              </p>
            )}
            {requiresCertificationPermission && !canCertify && (
              <p className="cert-message cert-message--error">
                {t("cert.notAuthorized")}
              </p>
            )}
          </div>

          <form className="cert-form" onSubmit={handleSubmit} onKeyDown={handleFormKeyDown}>
            <Field
              id="certification-authority"
              label={t("cert.fields.authority.label")}
              hint={t("cert.fields.authority.hint")}
              error={touched.auth && validationErrors.auth ? t(validationErrors.auth) : null}
            >
              <select
                value={form.auth}
                onChange={handleChange("auth")}
                onBlur={markTouched("auth")}
                className="cert-input cert-input--select"
                disabled={requiresCertificationPermission && isCertificationEntity}
              >
                <option value="" disabled>
                  {t("cert.fields.authority.placeholder")}
                </option>
                {authorities.map((authority) => (
                  <option key={authority.slug} value={authority.slug}>
                    {authority.display_name}
                  </option>
                ))}
              </select>
            </Field>

            <Field
              id="certification-number"
              label={t("cert.fields.cert.label")}
              hint={t("cert.fields.cert.hint")}
              error={touched.cert && validationErrors.cert ? t(validationErrors.cert) : null}
            >
              <input
                value={form.cert}
                onChange={handleChange("cert")}
                onBlur={markTouched("cert")}
                type="text"
                placeholder={t("cert.fields.cert.placeholder")}
                className="cert-input"
                inputMode="numeric"
              />
            </Field>

            <Field
              id="grading-system"
              label={t("cert.fields.gradingSystem.label")}
              hint={t("cert.fields.gradingSystem.hint")}
              error={touched.gradingSystem && validationErrors.gradingSystem ? t(validationErrors.gradingSystem) : null}
            >
              <select
                value={form.gradingSystem}
                onChange={handleChange("gradingSystem")}
                onBlur={markTouched("gradingSystem")}
                className="cert-input cert-input--select"
              >
                {(gradingCatalog.systems?.length
                  ? gradingCatalog.systems
                  : [{ code: "sheldon" }, { code: "ancient_descriptive" }]
                ).map((system) => (
                  <option key={system.code} value={system.code}>
                    {t(`cert.fields.gradingSystem.options.${system.code}`, {
                      defaultValue: system.name || system.code,
                    })}
                  </option>
                ))}
              </select>
            </Field>

            {String(form.gradingSystem).toLowerCase() === "ancient_descriptive" ? (
              <>
                <Field
                  id="grade-term"
                  label={t("cert.fields.gradeTerm.label")}
                  hint={t("cert.fields.gradeTerm.hint")}
                  error={touched.termCode && validationErrors.termCode ? t(validationErrors.termCode) : null}
                >
                  <select
                    value={form.termCode}
                    onChange={handleChange("termCode")}
                    onBlur={markTouched("termCode")}
                    className="cert-input cert-input--select"
                  >
                    <option value="" disabled>
                      {t("cert.fields.gradeTerm.placeholder")}
                    </option>
                    {(gradingCatalog.terms?.ancient_descriptive || []).map((term) => (
                      <option key={term.code} value={term.code}>
                        {term.display_name || term.code}
                      </option>
                    ))}
                  </select>
                </Field>

                <Field
                  id="grade-strike"
                  label={t("cert.fields.gradeStrike.label")}
                  hint={t("cert.fields.gradeStrike.hint")}
                  error={touched.strike && validationErrors.strike ? t(validationErrors.strike) : null}
                >
                  <select
                    value={form.strike}
                    onChange={handleChange("strike")}
                    onBlur={markTouched("strike")}
                    className="cert-input cert-input--select"
                  >
                    <option value="">{t("cert.fields.optional")}</option>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <option key={value} value={value}>{`${value}/5`}</option>
                    ))}
                  </select>
                </Field>

                <Field
                  id="grade-surface"
                  label={t("cert.fields.gradeSurface.label")}
                  hint={t("cert.fields.gradeSurface.hint")}
                  error={touched.surface && validationErrors.surface ? t(validationErrors.surface) : null}
                >
                  <select
                    value={form.surface}
                    onChange={handleChange("surface")}
                    onBlur={markTouched("surface")}
                    className="cert-input cert-input--select"
                  >
                    <option value="">{t("cert.fields.optional")}</option>
                    {[1, 2, 3, 4, 5].map((value) => (
                      <option key={value} value={value}>{`${value}/5`}</option>
                    ))}
                  </select>
                </Field>

                <Field
                  id="grade-style"
                  label={t("cert.fields.gradeStyle.label")}
                  hint={t("cert.fields.gradeStyle.hint")}
                  error={touched.style && validationErrors.style ? t(validationErrors.style) : null}
                >
                  <input
                    value={form.style}
                    onChange={handleChange("style")}
                    onBlur={markTouched("style")}
                    type="text"
                    placeholder={t("cert.fields.gradeStyle.placeholder")}
                    className="cert-input"
                  />
                </Field>

                <Field
                  id="grade-details-flag"
                  label={t("cert.fields.gradeDetailsFlag.label")}
                  hint={null}
                >
                  <div className="cert-checkbox-group cert-checkbox-group--compact">
                    <label className="cert-checkbox">
                      <input
                        type="checkbox"
                        checked={Boolean(form.detailsFlag)}
                        onChange={handleChange("detailsFlag")}
                        onBlur={markTouched("detailsFlag")}
                      />
                      {t("cert.fields.gradeDetailsFlag.inline", {
                        defaultValue: "Toggle when the item has a details issue",
                      })}
                    </label>
                  </div>
                </Field>

                {form.detailsFlag && (
                  <Field
                    id="grade-details-reason"
                    label={t("cert.fields.gradeDetailsReason.label")}
                    hint={t("cert.fields.gradeDetailsReason.hint")}
                    error={touched.detailsReason && validationErrors.detailsReason ? t(validationErrors.detailsReason) : null}
                  >
                    <input
                      value={form.detailsReason}
                      onChange={handleChange("detailsReason")}
                      onBlur={markTouched("detailsReason")}
                      type="text"
                      placeholder={t("cert.fields.gradeDetailsReason.placeholder")}
                      className="cert-input"
                    />
                  </Field>
                )}
              </>
            ) : (
              <>
                <Field id="grade-mode" label={t("cert.fields.gradeMode.label", { defaultValue: "Grade type" })}>
                  <div className="cert-radio-inline">
                    <label className="cert-radio-inline__item">
                      <input
                        type="radio"
                        name="grade-mode"
                        value="numeric"
                        checked={form.gradeMode === "numeric"}
                        onChange={handleChange("gradeMode")}
                        onBlur={markTouched("gradeMode")}
                      />
                      <span className="cert-radio-inline__text">
                        {t("cert.fields.gradeMode.numeric", { defaultValue: "Numeric (1-70)" })}
                      </span>
                    </label>
                   <label className="cert-radio-inline__item">
                     <input
                       type="radio"
                       name="grade-mode"
                       value="details"
                       checked={form.gradeMode === "details"}
                       onChange={handleChange("gradeMode")}
                       onBlur={markTouched("gradeMode")}
                     />
                     <span className="cert-radio-inline__text">
                       {t("cert.fields.gradeMode.details", { defaultValue: "Details (UNC/AU/XF/VF/F/VG/G)" })}
                     </span>
                   </label>
                    <label className="cert-radio-inline__item">
                      <input
                        type="radio"
                        name="grade-mode"
                        value="authentic"
                        checked={form.gradeMode === "authentic"}
                        onChange={handleChange("gradeMode")}
                        onBlur={markTouched("gradeMode")}
                      />
                      <span className="cert-radio-inline__text">
                        {t("cert.fields.gradeMode.authentic", { defaultValue: "Authentic only (no grade)" })}
                      </span>
                    </label>
                 </div>
               </Field>

                {form.gradeMode === "details" ? (
                  <Field
                    id="details-term"
                    label={t("cert.fields.detailsTerm.label", { defaultValue: "Details grade" })}
                    hint={t("cert.fields.detailsTerm.hint", { defaultValue: "Select UNC/AU/XF/VF/F/VG/G Details" })}
                    error={touched.detailsTerm && validationErrors.detailsTerm ? t(validationErrors.detailsTerm) : null}
                  >
                    <select
                      value={form.detailsTerm}
                      onChange={handleChange("detailsTerm")}
                      onBlur={markTouched("detailsTerm")}
                      className="cert-input cert-input--select"
                    >
                      <option value="" disabled>
                        {t("cert.fields.detailsTerm.placeholder", { defaultValue: "Choose details grade" })}
                      </option>
                      {[
                        "UNC_DETAILS",
                        "AU_DETAILS",
                        "XF_DETAILS",
                        "VF_DETAILS",
                        "F_DETAILS",
                        "VG_DETAILS",
                        "G_DETAILS",
                        "AG_DETAILS",
                        "FR_DETAILS",
                        "PR_DETAILS",
                      ].map((code) => (
                        <option key={code} value={code}>
                          {code.replace("_DETAILS", "")} Details
                        </option>
                      ))}
                    </select>
                  </Field>
                ) : form.gradeMode === "authentic" ? null : (
                  <Field
                    id="grade"
                    label={t("cert.fields.grade.label")}
                    hint={t("cert.fields.grade.hint")}
                    error={touched.grade && validationErrors.grade ? t(validationErrors.grade) : null}
                  >
                    <input
                      value={form.grade}
                      onChange={handleChange("grade")}
                      onBlur={markTouched("grade")}
                      type="text"
                      placeholder={t("cert.fields.grade.placeholder")}
                      className="cert-input"
                    />
                  </Field>
                )}

                {form.gradeMode !== "authentic" && strikeOptions.length > 0 && (
                  <Field
                    id="strike-char"
                    label={t("cert.fields.strikeChar.label", { defaultValue: "Strike characters" })}
                    hint={
                      form.gradeMode === "details"
                        ? t("cert.fields.strikeChar.hintDetails", { defaultValue: "Optional RD/RB/BN" })
                        : t("cert.fields.strikeChar.hint", { defaultValue: "Optional RD/RB/BN/FB/FS" })
                    }
                  >
                    <select
                      value={form.strikeChar}
                      onChange={handleChange("strikeChar")}
                      onBlur={markTouched("strikeChar")}
                      className="cert-input cert-input--select"
                    >
                      <option value="">{t("cert.fields.optional")}</option>
                      {strikeOptions.map((code) => (
                        <option key={code} value={code.toLowerCase()}>
                          {code}
                        </option>
                      ))}
                    </select>
                  </Field>
                )}

                {form.gradeMode === "numeric" && (
                  <Field
                    id="bonus"
                    label={t("cert.fields.bonus.label", { defaultValue: "Bonus designations" })}
                    hint={t("cert.fields.bonus.hint", { defaultValue: "Optional + and/or ★" })}
                  >
                    <div className="cert-checkbox-group">
                      <label className="cert-checkbox">
                        <input
                          type="checkbox"
                          checked={Boolean(form.bonusPlus)}
                          onChange={handleChange("bonusPlus")}
                          onBlur={markTouched("bonusPlus")}
                        />
                        +
                      </label>
                      <label className="cert-checkbox">
                        <input
                          type="checkbox"
                          checked={Boolean(form.bonusStar)}
                          onChange={handleChange("bonusStar")}
                          onBlur={markTouched("bonusStar")}
                        />
                        ★
                      </label>
                    </div>
                  </Field>
                )}
              </>
            )}

            <Field
              id="country"
              label={t("cert.fields.country.label")}
              hint={t("cert.fields.country.hint")}
              error={touched.country && validationErrors.country ? t(validationErrors.country) : null}
            >
              <CountryAuthorityPicker
                id="country"
                value={form.country}
                selectedAuthority={selectedCountryAuthority}
                gradingSystem={form.gradingSystem}
                onChangeValue={handleCountryInputChange}
                onAuthoritySelect={handleCountrySelect}
                onClearAuthority={handleCountryClear}
                onBlur={markTouched("country")}
                placeholder={t("cert.fields.country.placeholder")}
                className="cert-input"
              />
            </Field>

            <Field
              id="denomination"
              label={t("cert.fields.denomination.label")}
              hint={t("cert.fields.denomination.hint")}
              error={touched.denomination && validationErrors.denomination ? t(validationErrors.denomination) : null}
            >
              <input
                value={form.denomination}
                onChange={handleChange("denomination")}
                onBlur={markTouched("denomination")}
                type="text"
                placeholder={t("cert.fields.denomination.placeholder")}
                className="cert-input"
              />
            </Field>

            <Field
              id="year"
              label={t("cert.fields.year.label")}
              hint={t("cert.fields.year.hint")}
              error={touched.year && validationErrors.year ? t(validationErrors.year) : null}
            >
              <input
                value={form.year}
                onChange={handleChange("year")}
                onBlur={markTouched("year")}
                type="number"
                placeholder={t("cert.fields.year.placeholder")}
                className="cert-input"
                inputMode="numeric"
              />
            </Field>

            <Field
              id="mint"
              label={t("cert.fields.mint.label")}
              hint={t("cert.fields.mint.hint")}
              error={touched.mint && validationErrors.mint ? t(validationErrors.mint) : null}
            >
              <MintPicker
                id="mint"
                value={form.mint}
                selectedMint={selectedMint}
                gradingSystem={form.gradingSystem}
                countryRefCode={selectedCountryAuthority?.code}
                year={form.year}
                onChangeValue={handleMintInputChange}
                onMintSelect={handleMintSelect}
                onClearMint={handleMintClear}
                onBlur={markTouched("mint")}
                placeholder={t("cert.fields.mint.placeholder")}
                className="cert-input"
              />
            </Field>

            <Field
              id="metal"
              label={t("cert.fields.metal.label")}
              hint={t("cert.fields.metal.hint")}
              error={touched.metal && validationErrors.metal ? t(validationErrors.metal) : null}
            >
              <FreeTextCombobox
                value={form.metal}
                onChange={handleChange("metal")}
                onBlur={markTouched("metal")}
                placeholder={t("cert.fields.metal.placeholder")}
                className="cert-input"
                options={metalSuggestions}
              />
            </Field>

            <Field
              id="slab-total-weight"
              label={t("cert.fields.slabWeight.label")}
              hint={t("cert.fields.slabWeight.hint", { unit: slabWeightUnitLabel })}
              error={touched.slab_total_weight && validationErrors.slab_total_weight ? t(validationErrors.slab_total_weight) : null}
            >
              <input
                value={form.slab_total_weight}
                onChange={handleChange("slab_total_weight")}
                onBlur={markTouched("slab_total_weight")}
                type="text"
                placeholder={slabWeightPlaceholder}
                className="cert-input"
                inputMode="decimal"
              />
            </Field>

            <Field
              id="collection"
              label={t("cert.fields.collection.label")}
              hint={t("cert.fields.collection.hint")}
              error={touched.collection && validationErrors.collection ? t(validationErrors.collection) : null}
            >
              <input
                value={form.collection}
                onChange={handleChange("collection")}
                onBlur={markTouched("collection")}
                type="text"
                placeholder={t("cert.fields.collection.placeholder")}
                className="cert-input"
              />
            </Field>

            {requiresCertificationPermission && (
              <>
                <Field
                  id="owner-mode"
                  label={t("cert.fields.ownerMode.label")}
                >
                  <div className="cert-radio-inline">
                    <label className="cert-radio-inline__item">
                      <input
                        type="radio"
                        name="owner-mode"
                        value="id"
                        checked={ownerMode === "id"}
                        onChange={() => setOwnerMode("id")}
                      />
                      <span className="cert-radio-inline__text">
                        {t("cert.fields.ownerMode.id")}
                      </span>
                    </label>
                    <label className="cert-radio-inline__item">
                      <input
                        type="radio"
                        name="owner-mode"
                        value="email"
                        checked={ownerMode === "email"}
                        onChange={() => setOwnerMode("email")}
                      />
                      <span className="cert-radio-inline__text">
                        {t("cert.fields.ownerMode.email")}
                      </span>
                    </label>
                  </div>
                </Field>

                <Field
                  id="numistrail-user-id"
                  label={ownerLabel}
                  hint={ownerHint}
                  error={ownerError}
                >
                  <input
                    value={ownerValue}
                    onChange={handleChange(ownerFieldKey)}
                    onBlur={markTouched(ownerFieldKey)}
                    type={ownerMode === "email" ? "email" : "text"}
                    placeholder={ownerPlaceholder}
                    className="cert-input"
                  />
                </Field>
              </>
            )}

            <Field
              id="description"
              label={t("cert.fields.desc.label")}
              hint={t("cert.fields.desc.hint")}
              error={touched.desc && validationErrors.desc ? t(validationErrors.desc) : null}
            >
              <textarea
                value={form.desc}
                onChange={handleChange("desc")}
                onBlur={markTouched("desc")}
                rows={3}
                placeholder={t("cert.fields.desc.placeholder")}
                className="cert-input cert-input--textarea"
              />
            </Field>

            {message ? (
              <p
                className={`cert-message ${
                  message.kind === "error"
                    ? "cert-message--error"
                    : "cert-message--success"
                }`}
                role={message.kind === "error" ? "alert" : "status"}
              >
                {message.text}
              </p>
            ) : null}

            <button
              type="submit"
              disabled={isSubmitDisabled}
              className="cert-button"
            >
              {isSubmitting ? submitProgressLabel : submitLabel}
            </button>
          </form>
      </Card>
    </div>
  );
}

function validateAuthority(value, slugSet, hasAuthorityOptions) {
  if (!value) {
    return "cert.errors.authorityRequired";
  }
  if (hasAuthorityOptions && !slugSet.has(value)) {
    return "cert.errors.authorityInvalid";
  }
  return null;
}
